import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import Dinero from 'dinero.js'
import { Button, CircularProgress, Stack, Box } from '@mui/material'
import { AddCircleOutline } from '@mui/icons-material'

import { useGetBenchmarksQuery, useCreateBenchmarkMutation, useUpdateBenchmarkMutation } from 'services/benchmarks'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalCustomActionBar from 'components/generic/modals/form-modal/FormModalCustomActionBar'
import DataGrid from 'components/generic/datagrid'
import benchmarkSchema from 'utils/validation/benchmark'
import { setSnackBar } from 'state/ui'
import { DEFAULT_PRECISION } from 'utils/constants'
import { dateFormatter, roundTo } from 'utils/data-manipulation/valueFormatter'
import { getValidationError, getColumns, getButtons } from './benchmarksHelpers'

const BenchmarkPricing = ({ onFormStateChange, onHideModal }) => {
  const { data: benchmarks, isLoading } = useGetBenchmarksQuery({}, { refetchOnMountOrArgChange: true })
  const [isEdit, setIsEdit] = useState(false)
  const [rows, setRows] = useState()
  const { handleSubmit, control, reset, setValue, formState } = useForm()
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length
  const { defaultCurrency, defaultYardUm } = useSelector(state => state.settings)

  const [createBenchmark, { isLoading: isCreating }] = useCreateBenchmarkMutation()
  const [updateBenchmark, { isLoading: isUpdating }] = useUpdateBenchmarkMutation()
  const dispatch = useDispatch()
  const buttons = getButtons({ isEdit, onHideModal, setIsEdit })

  useEffect(() => {
    onFormStateChange(isDirty)
  }, [isDirty])

  useEffect(() => {
    if (benchmarks) {
      setRows(
        benchmarks?.Items?.map(benchmark => {
          reset({
            [benchmark?.id]: {
              name: benchmark?.name,
              publication: benchmark?.publication,
              price: benchmark?.price,
            },
          })
          return {
            id: benchmark?.id,
            name: benchmark?.name,
            publication: benchmark?.publication,
            price: benchmark?.price,
            date: dateFormatter(benchmark?.date),
          }
        }),
      )
    }
  }, [benchmarks])

  const columns = getColumns(control, setValue, benchmarkSchema, isEdit)

  if (isLoading || !handleSubmit || !rows || isCreating || isUpdating) {
    return (
      <Box textAlign="center" lineHeight="100vh">
        <CircularProgress color="primary" />
      </Box>
    )
  }

  function addNewBenchmark() {
    const newBenchmark = {
      id: `new-${Math.floor(Math.random() * 10 ** 6)}`,
      currency: defaultCurrency.toLowerCase(),
      date: new Date().toLocaleDateString(),
    }
    setRows([...rows, newBenchmark])
  }

  const handleError = () => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: 'There was an error saving benchmarks.',
      }),
    )
  }

  const formatForValidation = data =>
    Object.entries(data).map(([key, benchmark]) => ({
      id: key,
      ...benchmark,
      price: typeof benchmark.price === 'object' ? Dinero({ ...benchmark.price }).toUnit() : benchmark.price,
    }))

  const formatForRequest = data =>
    data.map(benchmark => ({
      ...benchmark,
      units: defaultYardUm,
      price: {
        amount: roundTo((Number(benchmark.price) || 0) * 10 ** DEFAULT_PRECISION),
        precision: DEFAULT_PRECISION,
        currency: defaultCurrency,
      },
    }))

  async function submit(data) {
    const filteredData = {}
    Object.keys(dirtyFields).forEach(rowId => {
      filteredData[rowId] = data[rowId]
    })
    const validationData = formatForValidation(filteredData)
    const requestData = formatForRequest(validationData)

    // validate
    await Promise.all(
      validationData.map(async benchmark => {
        const { id } = benchmark
        const validationError = getValidationError(() => benchmarkSchema.validateSync(benchmark))
        if (validationError) {
          console.error('Benchmark', id, 'not valid!', validationError.message)
          throw validationError
        }
      }),
    )
      .then(() =>
        Promise.all(
          requestData.map(benchmark => {
            const { id } = benchmark
            if (id.substr(0, 4) === 'new-') {
              // create

              delete benchmark.id
              return createBenchmark({ benchmark }).unwrap()
            }
            // update
            return updateBenchmark({ benchmarkId: id, benchmark }).unwrap()
          }),
        ),
      )
      .then(() => {
        // success
        setIsEdit(false)
      })
      .catch(err => {
        // failure

        console.error(err)
        handleError()
      })
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Stack height="100vh">
        <FormModalHeading title="Benchmark Pricing" backButtonPosition="left" onBack={() => onHideModal()} />
        <Box flexGrow={1}>
          <DataGrid
            columns={columns}
            rows={rows}
            height="auto"
            flexGrow={1}
            hideFooter
            disableSelectionOnClick
            onCellClick={(param, event) => event.stopPropagation()}
          />
          {isEdit && (
            <Box p={1}>
              <Button onClick={event => addNewBenchmark(event)}>
                <AddCircleOutline /> Add Benchmark Price
              </Button>
            </Box>
          )}
        </Box>
        <FormModalCustomActionBar buttonConfigs={buttons} justify="flex-start" />
      </Stack>
    </form>
  )
}

BenchmarkPricing.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

BenchmarkPricing.defaultProps = {
  onFormStateChange: noop,
  onHideModal: noop,
}

export default BenchmarkPricing
